import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { AvailableLanguages } from "utils/types";

interface SpeakButtonProps {
  text?: string;
  language?: AvailableLanguages;
  label: string;
  className?: string;
}

const SPEECH_LANG: Record<AvailableLanguages, string> = {
  [AvailableLanguages.ES]: "es-ES",
  [AvailableLanguages.EN]: "en-US",
};

export const isSpeechSupported = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

const SpeakIconButton = styled.button<{ $speaking: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  padding: 0;
  margin-left: 6px;
  border-radius: 50%;
  border: 1px solid rgba(173, 216, 230, 0.35);
  background: ${({ $speaking }) =>
    $speaking ? "rgba(173, 216, 230, 0.4)" : "rgba(24, 40, 72, 0.32)"};
  color: rgba(245, 226, 200, 0.95);
  cursor: pointer;
  font-size: 0.8rem;
  line-height: 1;
  vertical-align: middle;
  transition:
    background 120ms ease,
    transform 120ms ease;

  &:hover {
    background: rgba(173, 216, 230, 0.32);
  }

  &:active {
    transform: scale(0.92);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

export const SpeakButton = ({
  text,
  language,
  label,
  className,
}: SpeakButtonProps) => {
  const [speaking, setSpeaking] = useState(false);

  useEffect(
    () => () => {
      if (isSpeechSupported()) window.speechSynthesis.cancel();
    },
    []
  );

  const handleClick = useCallback(() => {
    if (!isSpeechSupported() || !text) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = SPEECH_LANG[language ?? AvailableLanguages.EN];
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [text, language]);

  if (!isSpeechSupported() || !text) return null;

  return (
    <SpeakIconButton
      type="button"
      className={className}
      onClick={handleClick}
      $speaking={speaking}
      aria-label={label}
      aria-pressed={speaking}
      title={label}
    >
      <span aria-hidden="true">{speaking ? "🔊" : "🔈"}</span>
    </SpeakIconButton>
  );
};
